import type { GameEvent, GameState } from "./game";

export type PlayerRole = "left" | "right" | "spectator";
export type PlayerSide = "left" | "right";
export interface PlayerNames { left: string; right: string }
export interface RocketState {
  id: number;
  owner: PlayerSide;
  x: number;
  y: number;
  vx: number;
}
export interface CombatState {
  rockets: RocketState[];
  shields: Record<PlayerSide, boolean>;
  damage: Record<PlayerSide, number[]>;
}
export type ClientMessage =
  | { type: "move"; direction: -1 | 0 | 1; seq: number }
  | { type: "start"; seq: number }
  | { type: "play-ai"; seq: number }
  | { type: "fire"; seq: number }
  | { type: "shield"; active: boolean; seq: number }
  | { type: "ping"; sentAt: number };

export type ServerMessage =
  | { type: "role"; role: PlayerRole; room: string }
  | {
      type: "state";
      tick: number;
      state: GameState;
      events: GameEvent[];
      players: number;
      names: PlayerNames;
      connected: Record<PlayerSide, boolean>;
      combat: CombatState;
      canPlayJay?: boolean;
    }
  | { type: "pong"; sentAt: number; serverAt: number }
  | { type: "error"; message: string };

const MAX_MESSAGE_BYTES = 1024;

export function normalizePlayerName(value: string | null): string {
  return (value ?? "")
    .toUpperCase()
    .replace(/[^A-Z0-9 '-]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 12)
    .trim();
}

export function resolvePlayerNames(
  incoming: PlayerNames | undefined,
  fallback: PlayerNames,
): PlayerNames {
  return incoming ?? fallback;
}

export function createCombatState(): CombatState {
  return {
    rockets: [],
    shields: { left: false, right: false },
    damage: { left: [], right: [] },
  };
}

export function resolveCombatState(incoming: unknown): CombatState {
  if (!incoming || typeof incoming !== "object") return createCombatState();
  const candidate = incoming as Partial<CombatState>;
  const rockets = candidate.rockets;
  const shields = candidate.shields;
  const damage = candidate.damage;
  if (
    !Array.isArray(rockets)
    || !rockets.every((rocket) => (
      rocket && typeof rocket === "object"
      && Number.isSafeInteger(rocket.id)
      && (rocket.owner === "left" || rocket.owner === "right")
      && [rocket.x, rocket.y, rocket.vx].every((value) => typeof value === "number" && Number.isFinite(value))
    ))
    || !shields || typeof shields.left !== "boolean" || typeof shields.right !== "boolean"
    || !damage
    || ![damage.left, damage.right].every((segments) => (
      Array.isArray(segments)
      && segments.every((segment) => Number.isInteger(segment) && segment >= 0 && segment < 5)
    ))
  ) return createCombatState();
  return {
    rockets: rockets.map((rocket) => ({ ...rocket })),
    shields: { ...shields },
    damage: { left: [...damage.left], right: [...damage.right] },
  };
}

export function parseClientMessage(raw: string): ClientMessage | null {
  if (raw.length > MAX_MESSAGE_BYTES) return null;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!value || typeof value !== "object") return null;
  const message = value as Record<string, unknown>;
  if (message.type === "move") {
    if (![-1, 0, 1].includes(message.direction as number)) return null;
    if (!Number.isSafeInteger(message.seq) || (message.seq as number) < 0) return null;
    return {
      type: "move",
      direction: message.direction as -1 | 0 | 1,
      seq: message.seq as number,
    };
  }
  if (message.type === "start") {
    if (!Number.isSafeInteger(message.seq) || (message.seq as number) < 0) return null;
    return { type: "start", seq: message.seq as number };
  }
  if (message.type === "play-ai") {
    if (!Number.isSafeInteger(message.seq) || (message.seq as number) < 0) return null;
    return { type: "play-ai", seq: message.seq as number };
  }
  if (message.type === "fire") {
    if (!Number.isSafeInteger(message.seq) || (message.seq as number) < 0) return null;
    return { type: "fire", seq: message.seq as number };
  }
  if (message.type === "shield") {
    if (typeof message.active !== "boolean") return null;
    if (!Number.isSafeInteger(message.seq) || (message.seq as number) < 0) return null;
    return {
      type: "shield", active: message.active, seq: message.seq as number,
    };
  }
  if (message.type === "ping" && typeof message.sentAt === "number" && Number.isFinite(message.sentAt)) {
    return { type: "ping", sentAt: message.sentAt };
  }
  return null;
}
